import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { useAuthStore } from '@/lib/stores/authStore';
import { BlockCard } from './BlockCard';
import { HOME_VIDEO_URL } from './homeVideoUrl';
import { DraftBanner, type CalculatorDraft } from './DraftBanner';
import { HomeHeader } from './HomeHeader';
import { Separator } from './Separator';
import { SubscribeCTA } from './SubscribeCTA';
import { TrialBanner } from './TrialBanner';
import type { HomeBlock, HomeItem } from './types';

/**
 * Pure presenter Главной (Экран 2.0). Не знает о навигации, fetch, useState —
 * только props → JSX. Container `Home.tsx` оркестрирует state и handlers.
 *
 * Layout сверху вниз: HomeHeader → TrialBanner (если showTrial) → greeting H1 →
 * DraftBanner (если draft) → список карточек блоков (BlockCard + Separator) →
 * SubscribeCTA → BottomBar (variant='home').
 *
 * v0.4 (ADR-0023): welcome heading «{userName}, привет! Чем помочь?» через
 * `useAuthStore((s) => s.profile.name)` с fallback `'Мебельщик'` при пустом
 * профиле (guest mode). Heading centered (text-center). v0.4.1 (H2.3):
 * fallback capitalize'ом — приветствие выглядит уважительнее, чем строчное.
 *
 * Источник истины layout — canonical spike
 * `docs/prototypes/_design_spike/screen-home-spike.html`.
 */
export interface HomeViewProps {
  blocks: readonly HomeItem[];
  showTrial: boolean;
  trialDays: number;
  draft?: CalculatorDraft;
  onTrialClick: () => void;
  onDraftClick: (draft: CalculatorDraft) => void;
  onBlockClick: (block: HomeBlock) => void;
  onSubscribeClick: () => void;
  onLogout: () => void;
}

function isBlock(item: HomeItem): item is HomeBlock {
  return item.type === 'block';
}

export function HomeView({
  blocks,
  showTrial,
  trialDays,
  draft,
  onTrialClick,
  onDraftClick,
  onBlockClick,
  onSubscribeClick,
  onLogout,
}: HomeViewProps) {
  const profileName = useAuthStore((s) => s.profile?.name);
  const userName = profileName && profileName.trim().length > 0 ? profileName : 'Мебельщик';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <HomeHeader />
      {showTrial && <TrialBanner trialDays={trialDays} onClick={onTrialClick} />}

      <section className="px-4 py-6">
        {/* v0.4.2 (H2.4): text-h1 → text-h2 (40px → 28px, ÷1.5) после Aibek visual verify —
            heading доминировал над контентом, новый размер пропорциональнее. */}
        <h1 className="font-display text-h2 text-ink text-center">
          {userName}, привет! Чем помочь?
        </h1>
      </section>

      {draft && <DraftBanner draft={draft} onClick={onDraftClick} />}

      <section className="px-4 pb-8 flex flex-col gap-2.5 flex-1">
        <VideoButton videoUrl={HOME_VIDEO_URL} />
        {blocks.map((item, idx) =>
          isBlock(item) ? (
            // id=1 «Видеоинструкция» рендерится через <VideoButton> выше,
            // в HOME_BLOCKS остаётся как метаданные (порядок/мапинг id),
            // но в presenter скипается чтобы не дублировать UI.
            item.id === 1 ? null : (
              <BlockCard key={item.id} block={item} onClick={onBlockClick} />
            )
          ) : (
            <Separator key={`sep-${idx}`} label={item.label} />
          ),
        )}
        <SubscribeCTA onClick={onSubscribeClick} />
      </section>

      <BottomBar variant="home" onLogout={onLogout} />
    </main>
  );
}
